import { useTranslation } from 'react-i18next';
import { FolderDetail, FolderLink } from '../../../services/api/folderService';
import { ImplantMemberItem } from '../ImplantMemberItem';
import { ScannerMemberItem } from '../ScannerMemberItem';
import { PolicyMemberItem } from '../PolicyMemberItem';
import { FileMemberItem } from '../FileMemberItem';
import { LinkMemberItem } from '../LinkMemberItem';
import { FolderSection } from './FolderSection';
import { SectionRows } from './SectionRows';

type ManageTarget = 'implants' | 'scanners' | 'policies' | 'files';

interface FolderMembersProps {
    folder: FolderDetail;
    canManage: boolean;
    onManage: (target: ManageTarget) => void;
    onSelectScanner: (scannerId: string) => void;
    onAddLink: () => void;
    onEditLink: (link: FolderLink) => void;
    onRemoveLink: (link: FolderLink) => void;
    removingLinkId: string | null;
}

export const FolderMembers = ({
    folder,
    canManage,
    onManage,
    onSelectScanner,
    onAddLink,
    onEditLink,
    onRemoveLink,
    removingLinkId,
}: FolderMembersProps) => {
    const { t } = useTranslation();
    const { implants, scanners, policies, files, links } = folder;

    const manageAction = (target: ManageTarget) =>
        canManage
            ? {
                  label: t('folders.detail.actions.manage'),
                  onClick: () => onManage(target),
              }
            : undefined;

    return (
        <>
            {implants.length > 0 && (
                <FolderSection
                    title={t('folders.detail.sections.implants')}
                    count={implants.length}
                    action={manageAction('implants')}
                >
                    <SectionRows>
                        {implants.map((implant) => (
                            <ImplantMemberItem
                                key={implant._id}
                                implant={implant}
                            />
                        ))}
                    </SectionRows>
                </FolderSection>
            )}

            {scanners.length > 0 && (
                <FolderSection
                    title={t('folders.detail.sections.scanners')}
                    count={scanners.length}
                    action={manageAction('scanners')}
                >
                    <SectionRows>
                        {scanners.map((scanner) => (
                            <ScannerMemberItem
                                key={scanner._id}
                                scanner={scanner}
                                onSelect={onSelectScanner}
                            />
                        ))}
                    </SectionRows>
                </FolderSection>
            )}

            {policies.length > 0 && (
                <FolderSection
                    title={t('folders.detail.sections.policies')}
                    count={policies.length}
                    action={manageAction('policies')}
                >
                    <SectionRows>
                        {policies.map((policy) => (
                            <PolicyMemberItem
                                key={policy._id}
                                policy={policy}
                            />
                        ))}
                    </SectionRows>
                </FolderSection>
            )}

            {files.length > 0 && (
                <FolderSection
                    title={t('folders.detail.sections.files')}
                    count={files.length}
                    action={manageAction('files')}
                >
                    <SectionRows>
                        {files.map((file) => (
                            <FileMemberItem key={file._id} file={file} />
                        ))}
                    </SectionRows>
                </FolderSection>
            )}

            {links.length > 0 && (
                <FolderSection
                    title={t('folders.detail.sections.links')}
                    count={links.length}
                    action={
                        canManage
                            ? {
                                  label: t('folders.links.add'),
                                  onClick: onAddLink,
                              }
                            : undefined
                    }
                >
                    <SectionRows>
                        {links.map((link) => (
                            <LinkMemberItem
                                key={link._id}
                                link={link}
                                onEdit={canManage ? onEditLink : undefined}
                                onRemove={canManage ? onRemoveLink : undefined}
                                removing={removingLinkId === link._id}
                            />
                        ))}
                    </SectionRows>
                </FolderSection>
            )}
        </>
    );
};
